import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { ApiMessage } from '@/services/apiMessage/apiMessage.types';
import { z } from 'zod';
import { useAddApiMessage } from './hooks/useAddApiMessage';
import { useUpdateApiMessage } from './hooks/useUpdateApiMessage';

const formSchema = z.object({
  code: z.string().trim().min(1, { message: 'common.form.validation.required' }),
  message: z.string().trim().min(1, { message: 'common.form.validation.required' }),
  messageEng: z.string().trim().min(1, { message: 'common.form.validation.required' }),
  httpStatusCode: z.string().trim().min(1, { message: 'common.form.validation.required' }),
  description: z.string().trim().min(1, { message: 'common.form.validation.required' }),
  category: z.string().min(1, { message: 'common.form.validation.required' }),
});

const categoryOptions = ['success', 'error', 'validation'].map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

export const ManageApiMessageForm = ({
  apiMessage,
  viewOnly = false,
}: {
  apiMessage?: ApiMessage;
  viewOnly?: boolean;
}) => {
  const { mutateAsync: addApiMessage } = useAddApiMessage();
  const { mutateAsync: updateApiMessage } = useUpdateApiMessage();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues: {
      code: apiMessage?.code ?? '',
      message: apiMessage?.message ?? '',
      messageEng: apiMessage?.messageEng ?? '',
      httpStatusCode: apiMessage?.httpStatusCode ?? '',
      description: apiMessage?.description ?? '',
      category: apiMessage?.category?.toLowerCase() ?? '',
    },
  });

  return (
    <Form
      className="overflow-y-auto"
      form={form}
      onSubmit={async (data) => {
        const { message, messageEng, httpStatusCode, description, category } = data;
        const fields = { message, messageEng, httpStatusCode, description, category };

        if (apiMessage) {
          await updateApiMessage({
            apiMessageId: apiMessage.apiMessageId,
            messageType: apiMessage.messageType,
            ...fields,
          });
          return;
        }

        await addApiMessage({ code: data.code, messageType: '', ...fields });
      }}
    >
      <fieldset disabled={viewOnly} className="w-full flex flex-col gap-4.5 px-1">
        <Input
          label="logsManagement.page.apilogs.form.code"
          readOnly={Boolean(apiMessage)}
          {...form.register('code')}
        />
        <Input label="logsManagement.page.apilogs.form.message" {...form.register('message')} />
        <Input label="logsManagement.page.apilogs.form.engmessage" {...form.register('messageEng')} />
        <Input label="logsManagement.page.apilogs.form.statusCode" {...form.register('httpStatusCode')} />
        <Textarea label="logsManagement.page.apilogs.form.description" {...form.register('description')} />
        <Select
          label="logsManagement.page.apilogs.form.categorytype"
          {...form.register('category')}
          options={categoryOptions}
          placeholder="Select Category Type"
        />
      </fieldset>
    </Form>
  );
};
